import { DetailedDriveStatistics } from '@/api/main/types.gen'
import { secondsToHHMM } from '@/utils/datetime'
import { percentage } from '@/utils/math'
import { formatDateTime } from '@/utils/datetime'
import { Badge } from '@mantine/core'
import { GenericTable, TableColumn } from './Table/GenericTable'
import { useTable } from './Table/useTable'
import { Text } from '@mantine/core'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

function DrivesTable({ driveStatistics }: { driveStatistics: DetailedDriveStatistics[] }) {
  const { t } = useTranslation('statistics')

  const driveTableHeaders: TableColumn<DetailedDriveStatistics>[] = [
    {
      key: 'drive_id',
      label: t('driveId'),
      visible: true,
      sortable: true,
      render: (item: DetailedDriveStatistics) => (
        <Link
          to="/drive-details/$id"
          params={{ id: item.drive_id.toString() }}
          style={{
            color: 'var(--mantine-color-blue-6)',
            textDecoration: 'underline',
          }}
        >
          {item.drive_id}
        </Link>
      ),
    },
    { key: 'vehicle_id', label: t('vehicleId'), visible: true, sortable: true },
    {
      key: 'drivers',
      label: t('info'),
      visible: true,
      sortable: false,
      render: (item: DetailedDriveStatistics) => <span>{item.drivers.map(d => d.name).join(', ')}</span>,
    },
    {
      key: 'start_index',
      label: t('routeFragment'),
      visible: true,
      sortable: true,
      align: 'right',
      render: (item: DetailedDriveStatistics) =>
        item.start_index === 0 && item.end_index === item.route_plan_item_count
          ? '100%'
          : `${percentage(item.start_index, item.route_plan_item_count)}-${percentage(
              item.end_index,
              item.route_plan_item_count
            )}`,
    },
    {
      key: 'state',
      label: t('state'),
      visible: true,
      sortable: true,
      render: (item: DetailedDriveStatistics) =>
        item.state === 'cancelled_in_progress' ? (
          <Badge variant="light" color="gray">
            {t('cancelled')}
          </Badge>
        ) : (
          <Badge color="blue">{t('completed')}</Badge>
        ),
    },
    {
      key: 'drive_started_at',
      label: t('startedAt'),
      visible: true,
      sortable: true,
      render: (item: DetailedDriveStatistics) => formatDateTime(item.drive_started_at ?? ''),
    },
    {
      key: 'duration_seconds',
      label: t('duration'),
      visible: true,
      sortable: true,
      render: (item: DetailedDriveStatistics) => (
        <Text style={{ textAlign: 'right' }}>{secondsToHHMM(item.duration_seconds)}</Text>
      ),
    },
  ]

  const table = useTable({
    data: driveStatistics,
    columns: driveTableHeaders,
    tableKey: 'drivesTableColumns',
    defaultSortField: 'drive_id',
    defaultSortDirection: 'desc',
    itemsPerPage: 20,
    searchDebounceMs: 0,
  })

  return (
    <GenericTable
      {...table.tableProps}
      isLoading={false}
      error={null}
      getRowKey={item => item.drive_id}
      emptyMessage={t('noDrivesFoundForRoute')}
      verticalSpacing="xs"
      hideCsvDownload={false}
      csvFileName="route-drives.csv"
    />
  )
}

export default DrivesTable
